import { useRef, useState } from "react";
import { Autocomplete, TextField, CircularProgress, Box, InputAdornment } from "@mui/material";
import { Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getData } from "../../services/base.service";

// Flattened shape used to feed a single grouped Autocomplete from the three result arrays
// the backend returns (experts / references / partners), each shaped as { id, label }.
type SearchResultGroup = 'Experts' | 'References' | 'Partners';
type SearchResultItem = {
  id: number;
  label: string;
  group: SearchResultGroup;
};

// Same debounce-after-N-characters shape used by ExpertsFilters' reference-search picker
// and EditableDataGrid's preselection search box.
const SEARCH_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

// Maps each result group to the route it should jump to. References navigate by project
// number - the backend's `id` for a reference result IS the project number (see the
// GET api/search contract: { id: 5955, label: "5955 - ..." }), matching how
// ReferenceExpertActionsCell/ExpertForm's Project History tab already navigate to
// `/references/${projectNo}`.
const buildRoute = (item: SearchResultItem): string => {
  switch (item.group) {
    case 'Experts':
      return `/experts/${item.id}`;
    case 'References':
      return `/references/${item.id}`;
    case 'Partners':
      return `/partners/${item.id}`;
    default:
      return '/';
  }
};

function GlobalSearchBar() {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleInputChange = (event: any, newInputValue: string, reason: string) => {
    setInputValue(newInputValue);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    // 'reset' fires when an option is picked/cleared - don't re-search on that.
    if (reason === 'reset') return;

    const trimmed = (newInputValue || '').trim();
    if (trimmed.length < SEARCH_MIN_LENGTH) {
      setOptions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await getData(`search?q=${encodeURIComponent(trimmed)}`);
        const flattened: SearchResultItem[] = [
          ...((result?.experts || []) as any[]).map((e) => ({ id: e.id, label: e.label, group: 'Experts' as const })),
          ...((result?.references || []) as any[]).map((r) => ({ id: r.id, label: r.label, group: 'References' as const })),
          ...((result?.partners || []) as any[]).map((p) => ({ id: p.id, label: p.label, group: 'Partners' as const })),
        ];
        setOptions(flattened);
      } catch (err) {
        // Backend may not be reachable/implemented yet - degrade to "no results" rather than crash.
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSelect = (event: any, newValue: SearchResultItem | null) => {
    if (!newValue) return;
    navigate(buildRoute(newValue));
    setInputValue('');
    setOptions([]);
  };

  return (
    <Box sx={{ width: { xs: '100%', sm: 280 }, marginRight: 2 }}>
      <Autocomplete
        size="small"
        options={options}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.label || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id && option.group === value.group}
        filterOptions={(opts) => opts} // results already filtered server-side
        loading={loading}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleSelect}
        clearOnBlur={false}
        blurOnSelect
        noOptionsText={
          inputValue.trim().length < SEARCH_MIN_LENGTH
            ? `Type at least ${SEARCH_MIN_LENGTH} characters to search`
            : 'No results found'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search experts, references, partners..."
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: 1,
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&:hover fieldset': { borderColor: 'white' },
                '&.Mui-focused fieldset': { borderColor: 'white' },
              },
              '& .MuiInputBase-input::placeholder': { color: 'rgba(255, 255, 255, 0.85)', opacity: 1 },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'white' }} fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}

export default GlobalSearchBar;
